import { BEHAVIOUR_TYPES, defaultBusinessConfig } from './behaviourCatalog';
import { compileField } from './buildFormulaFromBehaviour';
import { uniqueCode } from './fieldCodeSuggest';
import { departmentRef } from './astHelpers';

/**
 * Common RA field presets — starter configurations only (rates are defaults, not locked law).
 * Department FY rate keys: security_deposit_pct, gst_pct, income_tax_pct, labour_cess_pct
 */
export const DEPARTMENT_FY_RATE_KEYS = [
  { value: 'security_deposit_pct', label: 'FY Security Deposit %', defaultValue: 2.5 },
  { value: 'gst_pct', label: 'FY GST TDS %', defaultValue: 2 },
  { value: 'income_tax_pct', label: 'FY Income Tax TDS %', defaultValue: 2 },
  { value: 'labour_cess_pct', label: 'FY Labour Cess %', defaultValue: 1 },
];

export const FIELD_PRESETS = [
  {
    id: 'FY_SD_RATE',
    label: 'FY Security Deposit Rate %',
    sectionHint: 'RECOVERIES',
    dataType: 'PERCENTAGE',
    departmentFieldCode: 'security_deposit_pct',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.AUTO_LOOKUP,
      financialClassification: 'REFERENCE',
      financialEffect: 'INFORMATION',
      includeInGroup: 'NONE',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'security_deposit_pct',
      rateValue: 2.5,
      comparisonBehaviour: 'NO_COMPARISON',
      previousBehaviour: 'NONE',
    }),
  },
  {
    id: 'FY_GST_RATE',
    label: 'FY GST TDS Rate %',
    sectionHint: 'RECOVERIES',
    dataType: 'PERCENTAGE',
    departmentFieldCode: 'gst_pct',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.AUTO_LOOKUP,
      financialClassification: 'REFERENCE',
      financialEffect: 'INFORMATION',
      includeInGroup: 'NONE',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'gst_pct',
      rateValue: 2,
      comparisonBehaviour: 'NO_COMPARISON',
      previousBehaviour: 'NONE',
    }),
  },
  {
    id: 'FY_IT_RATE',
    label: 'FY Income Tax Rate %',
    sectionHint: 'RECOVERIES',
    dataType: 'PERCENTAGE',
    departmentFieldCode: 'income_tax_pct',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.AUTO_LOOKUP,
      financialClassification: 'REFERENCE',
      financialEffect: 'INFORMATION',
      includeInGroup: 'NONE',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'income_tax_pct',
      rateValue: 2,
      comparisonBehaviour: 'NO_COMPARISON',
      previousBehaviour: 'NONE',
    }),
  },
  {
    id: 'FY_LABOUR_CESS_RATE',
    label: 'FY Labour Cess Rate %',
    sectionHint: 'RECOVERIES',
    dataType: 'PERCENTAGE',
    departmentFieldCode: 'labour_cess_pct',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.AUTO_LOOKUP,
      financialClassification: 'REFERENCE',
      financialEffect: 'INFORMATION',
      includeInGroup: 'NONE',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'labour_cess_pct',
      rateValue: 1,
      comparisonBehaviour: 'NO_COMPARISON',
      previousBehaviour: 'NONE',
    }),
  },
  {
    id: 'SECURITY_DEPOSIT',
    label: 'Security Deposit',
    sectionHint: 'RECOVERIES',
    semanticRole: 'SECURITY_DEPOSIT',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.CAPPED_PERCENTAGE,
      financialClassification: 'SECURITY_DEPOSIT',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'security_deposit_pct',
      rateValue: 2.5,
      baseFieldCode: 'NET_EXCL_SECURED_ADVANCE',
      previousBehaviour: 'SAME_FIELD',
    }),
  },
  {
    id: 'GST_TDS',
    label: 'GST TDS',
    sectionHint: 'RECOVERIES',
    semanticRole: 'GST_DEDUCTION',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.PERCENTAGE,
      financialClassification: 'STATUTORY_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'gst_pct',
      rateValue: 2,
      baseFieldCode: 'NET_EXCL_SECURED_ADVANCE',
      lineReference: 'b)',
      previousBehaviour: 'SAME_FIELD',
    }),
  },
  {
    id: 'INCOME_TAX',
    label: 'Income Tax TDS',
    sectionHint: 'RECOVERIES',
    semanticRole: 'INCOME_TAX',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.PERCENTAGE,
      financialClassification: 'STATUTORY_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'income_tax_pct',
      rateValue: 2,
      baseFieldCode: 'NET_EXCL_SECURED_ADVANCE',
      previousBehaviour: 'SAME_FIELD',
    }),
  },
  {
    id: 'LABOUR_CESS',
    label: 'Labour Cess',
    sectionHint: 'RECOVERIES',
    semanticRole: 'LABOUR_CESS',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.PERCENTAGE,
      financialClassification: 'STATUTORY_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
      rateSource: 'DEPARTMENT',
      rateFieldCode: 'labour_cess_pct',
      rateValue: 1,
      baseFieldCode: 'NET_EXCL_SECURED_ADVANCE',
      previousBehaviour: 'SAME_FIELD',
    }),
  },
  {
    id: 'RETENTION',
    label: 'Retention',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.CAPPED_PERCENTAGE,
      financialClassification: 'RETENTION',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
      rateSource: 'AGREEMENT',
      rateValue: 5,
      baseFieldCode: 'NET_EXCL_SECURED_ADVANCE',
      previousBehaviour: 'SAME_FIELD',
    }),
  },
  {
    id: 'MOB_ADV_RECOVERY',
    label: 'Mobilisation Advance Recovery',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.BALANCE_RECOVERY,
      financialClassification: 'ADVANCE_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
      rateSource: 'FIXED',
      rateValue: 10,
      baseFieldCode: 'CURRENT_RA_VALUE',
      originalAdvanceFieldCode: 'MOBILISATION_ADVANCE',
      previousOutstandingFieldCode: 'MOBILISATION_ADVANCE',
      recoveryMethod: 'PERCENTAGE',
      capToOutstanding: true,
      previousBehaviour: 'SAME_FIELD',
    }),
  },
  {
    id: 'WATER_CHARGES',
    label: 'Water Charges',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
      financialClassification: 'WATER_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
    }),
  },
  {
    id: 'ELECTRICITY_CHARGES',
    label: 'Electricity Charges',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
      financialClassification: 'ELECTRICITY_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
    }),
  },
  {
    id: 'MATERIAL_RECOVERY',
    label: 'Material Recovery',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
      financialClassification: 'CLIENT_MATERIAL_RECOVERY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
    }),
  },
  {
    id: 'PENALTY',
    label: 'Penalty',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
      financialClassification: 'PENALTY',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
    }),
  },
  {
    id: 'PRICE_ESCALATION',
    label: 'Price Escalation',
    sectionHint: 'WORK_VALUE',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
      financialClassification: 'PRICE_ADJUSTMENT',
      financialEffect: 'ADD',
      includeInGroup: 'GROSS_VALUE',
    }),
  },
  {
    id: 'ADDL_SD',
    label: 'Additional SD',
    sectionHint: 'RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
      financialClassification: 'SECURITY_DEPOSIT',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
    }),
  },
  {
    id: 'WITHHELD',
    label: 'Withheld',
    sectionHint: 'WITHHELD',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.WITHHELD,
      financialClassification: 'WITHHELD',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_WITHHELD',
    }),
  },
  {
    id: 'RELEASED_WITHHELD',
    label: 'Released Withheld',
    sectionHint: 'WITHHELD',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.RELEASE,
      financialClassification: 'RELEASE',
      financialEffect: 'ADD',
      includeInGroup: 'NONE',
    }),
  },
  {
    id: 'TOTAL_RECOVERIES',
    label: 'Total Recoveries',
    sectionHint: 'RECOVERIES',
    semanticRole: 'TOTAL_RECOVERIES',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.AUTO_TOTAL,
      financialClassification: 'CERTIFICATION_TOTAL',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_RECOVERIES',
    }),
  },
  {
    id: 'TOTAL_DEDUCTIONS',
    label: 'Total Deductions',
    sectionHint: 'NET_CERTIFICATION',
    semanticRole: 'TOTAL_DEDUCTIONS',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.AUTO_TOTAL,
      financialClassification: 'CERTIFICATION_TOTAL',
      financialEffect: 'DEDUCT',
      includeInGroup: 'TOTAL_DEDUCTIONS',
    }),
  },
  {
    id: 'NET_PAYABLE',
    label: 'Net Payable',
    sectionHint: 'NET_CERTIFICATION',
    semanticRole: 'NET_PAYABLE',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.SUBTRACT_FIELDS,
      financialClassification: 'PAYMENT',
      financialEffect: 'INFORMATION',
      includeInGroup: 'NONE',
      subtractFieldCodes: ['CURRENT_RA_VALUE', 'TOTAL_DEDUCTIONS'],
      minuendFieldCode: 'CURRENT_RA_VALUE',
      sumFieldCodes: ['TOTAL_DEDUCTIONS'],
    }),
  },
  {
    id: 'GROSS_UPTO_DATE',
    label: 'Gross Upto Date',
    sectionHint: 'CURRENT_CERTIFICATE',
    semanticRole: 'GROSS_UPTO_DATE',
    dataType: 'AMOUNT',
    businessConfig: defaultBusinessConfig({
      behaviourType: BEHAVIOUR_TYPES.SUM_OF_FIELDS,
      financialClassification: 'WORK_VALUE',
      financialEffect: 'ADD',
      includeInGroup: 'NONE',
      sumFieldCodes: [
        'GROSS_WORK_DONE',
        'SECURED_ADVANCE',
        'MOBILISATION_ADVANCE',
        'CLAUSE_10CC_10CA',
        'ADVANCE_NOT_MEASURED',
      ],
    }),
  },
];

export const ADD_FIELD_INTENTS = [
  { id: 'MANUAL', label: 'Manual Amount', behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY },
  { id: 'CALCULATED', label: 'Calculated Amount', behaviourType: BEHAVIOUR_TYPES.CUSTOM_FORMULA },
  { id: 'PERCENTAGE', label: 'Percentage / Deduction', behaviourType: BEHAVIOUR_TYPES.PERCENTAGE },
  { id: 'PREVIOUS', label: 'Previous RA Value', behaviourType: BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE },
  { id: 'TOTAL', label: 'Total', behaviourType: BEHAVIOUR_TYPES.AUTO_TOTAL },
  { id: 'REFERENCE', label: 'Text / Date / Reference', behaviourType: BEHAVIOUR_TYPES.REFERENCE_ONLY },
  { id: 'RECOVERY', label: 'Recovery', behaviourType: BEHAVIOUR_TYPES.PERCENTAGE },
  { id: 'ADVANCE_RECOVERY', label: 'Advance Recovery', behaviourType: BEHAVIOUR_TYPES.BALANCE_RECOVERY },
  { id: 'WITHHELD', label: 'Withheld / Release', behaviourType: BEHAVIOUR_TYPES.WITHHELD },
];

export const buildFieldFromPreset = (preset, { sectionCode, existingCodes = [] } = {}) => {
  const fieldCode = uniqueCode(preset.id || preset.label, existingCodes);
  const field = {
    fieldCode,
    fieldLabel: preset.label,
    sectionCode: sectionCode || preset.sectionHint || 'RECOVERIES',
    sequence: 0,
    dataType: preset.dataType || 'AMOUNT',
    fieldType: 'INPUT',
    sourceType: 'MANUAL',
    semanticRole: preset.semanticRole || null,
    formulaJson: null,
    editable: preset.businessConfig?.behaviourType === BEHAVIOUR_TYPES.MANUAL_ENTRY
      || preset.businessConfig?.behaviourType === BEHAVIOUR_TYPES.WITHHELD
      || preset.businessConfig?.behaviourType === BEHAVIOUR_TYPES.RELEASE,
    visible: true,
    printVisible: true,
    allowOverride: Boolean(preset.semanticRole === 'SECURITY_DEPOSIT'),
    status: 'ACTIVE',
    required: false,
    roundingMethod: preset.dataType === 'PERCENTAGE' ? 'NONE' : 'ROUND',
    decimalPlaces: preset.dataType === 'PERCENTAGE' ? 2 : 0,
    metadata: { businessConfig: { ...preset.businessConfig } },
  };
  const compiled = compileField(field, [field]);
  // FY rate % presets: resolve from department FY rule (with fixed fallback).
  if (preset.departmentFieldCode) {
    return {
      ...compiled,
      fieldType: 'FORMULA',
      sourceType: 'DEPARTMENT',
      editable: false,
      formulaJson: departmentRef(
        preset.departmentFieldCode,
        Number(preset.businessConfig?.rateValue) || 0,
      ),
      metadata: {
        ...(compiled.metadata || {}),
        businessConfig: {
          ...(compiled.metadata?.businessConfig || {}),
          rateSource: 'DEPARTMENT',
          rateFieldCode: preset.departmentFieldCode,
        },
        isDefaultFyRateField: true,
      },
    };
  }
  return compiled;
};

/** Default FY rate % fields to seed on new configurable templates. */
export const buildDefaultFyRateFields = (existingCodes = []) => {
  const codes = [...existingCodes];
  return FIELD_PRESETS
    .filter((p) => p.departmentFieldCode)
    .map((preset) => {
      const field = buildFieldFromPreset(preset, {
        sectionCode: preset.sectionHint || 'RECOVERIES',
        existingCodes: codes,
      });
      codes.push(field.fieldCode);
      return field;
    });
};
